// US-AUTH-01 + US-AUTH-03 + US-AUTH-07 login prompt.
//
// Owns:
//   - the password field (recovery/new-device mode)
//   - the 6-digit PIN field (the default return path when this account has
//     a PIN on this device)
//   - server-error handling for AppError.unauthorized (REQ-9.5)
//   - lockout state surfaced from auth_login's `locked` outcome
//     (REQ-9.6): refuses submission and ticks down a countdown until
//     the account unlocks. PIN lockout is tracked in the same local
//     signal since only one lockout is active at a time from the user's
//     point of view.

import {
  type AccountListEntry,
  type AppError,
  type AuthLoginResult,
  authLogin,
  biometricLogin,
  biometricStatusForAccount,
  pinLogin,
  pinStatus,
} from '@chorepop/core/client';
import {
  type Component,
  Show,
  createEffect,
  createResource,
  createSignal,
  onCleanup,
} from 'solid-js';
import { AppVersionFooter } from '../components/AppVersionFooter';
import { t } from '../i18n';

type Props = {
  account: AccountListEntry;
  onLoggedIn: (session: Extract<AuthLoginResult, { outcome: 'success' }>) => void;
  onCancel: () => void;
};

type Mode = 'password' | 'pin';

export const LoginPasswordScreen: Component<Props> = (props) => {
  const [mode, setMode] = createSignal<Mode>('password');
  const [password, setPassword] = createSignal('');
  const [pin, setPin] = createSignal('');
  const [submitting, setSubmitting] = createSignal(false);
  const [errorMessage, setErrorMessage] = createSignal<string | null>(null);
  const [lockedUntilMs, setLockedUntilMs] = createSignal<number | null>(null);
  const [now, setNow] = createSignal(Date.now());

  // Does this account have a PIN on this device? Drives whether the
  // "Use PIN instead" link is shown. Cheap pre-auth call.
  const [pinAvailable] = createResource(
    () => props.account.accountId,
    async (accountId) => {
      const r = await pinStatus({ accountId });
      if (!r.ok) return false;
      return r.value.hasPin;
    },
  );

  // Is biometric enabled for this account on this device?
  const [biometricStatus] = createResource(
    () => props.account.accountId,
    async (accountId) => {
      // Children never get a biometric route. Their PIN is intentionally the
      // only quick-unlock mechanism, so a child cannot use a device-level
      // biometric enrolment to access a guardian account.
      if (props.account.roleCode !== 'guardian') return { enabled: false, biometryLabel: null };
      const r = await biometricStatusForAccount({ accountId });
      if (!r.ok) return { enabled: false, biometryLabel: null };
      return r.value;
    },
  );
  const biometricButtonLabel = () =>
    `Sign in with ${biometricStatus()?.biometryLabel ?? 'biometrics'}`;

  const handleBiometricLogin = async () => {
    setSubmitting(true);
    setErrorMessage(null);
    try {
      const r = await biometricLogin({ accountId: props.account.accountId });
      if (!r.ok) {
        setErrorMessage('Biometric sign-in failed. Please use your password or PIN.');
        return;
      }
      const result = r.value;
      if (result.outcome === 'success') {
        props.onLoggedIn(result);
      } else if (result.outcome === 'cancelled') {
        // User cancelled -- no error message needed
      } else if (result.outcome === 'failed') {
        setErrorMessage('Biometric verification failed. Please try again or use your password.');
      } else {
        setErrorMessage('Biometric sign-in is not available. Please use your password or PIN.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  createEffect(() => {
    if (lockedUntilMs() === null) return;
    const handle = window.setInterval(() => setNow(Date.now()), 1000);
    onCleanup(() => window.clearInterval(handle));
  });

  createEffect(() => {
    const until = lockedUntilMs();
    if (until !== null && now() >= until) {
      setLockedUntilMs(null);
    }
  });

  const remainingSeconds = () => {
    const until = lockedUntilMs();
    if (until === null) return 0;
    return Math.max(0, Math.ceil((until - now()) / 1000));
  };

  const isLocked = () => lockedUntilMs() !== null && remainingSeconds() > 0;

  // A device PIN is the normal return path once it exists. Password remains
  // available for recovery; cloud identity is intentionally not a daily login.
  createEffect(() => {
    if (pinAvailable()) {
      setMode('pin');
    }
  });

  const flipToPin = () => {
    setMode('pin');
    setErrorMessage(null);
    setLockedUntilMs(null);
    setPin('');
  };

  const flipToPassword = () => {
    setMode('password');
    setErrorMessage(null);
    setLockedUntilMs(null);
    setPassword('');
  };

  const handlePasswordSubmit = async (e: Event) => {
    e.preventDefault();
    setErrorMessage(null);
    if (isLocked()) return;

    if (password().length === 0) {
      setErrorMessage('Please enter your password.');
      return;
    }

    setSubmitting(true);
    try {
      const result = await authLogin({
        accountId: props.account.accountId,
        password: password(),
      });
      if (!result.ok) {
        setErrorMessage(humanReadableError(result.error));
        return;
      }
      const outcome = result.value;
      if (outcome.outcome === 'locked') {
        setLockedUntilMs(new Date(outcome.unlocksAtUtc).getTime());
        setNow(Date.now());
        return;
      }
      setPassword('');
      props.onLoggedIn(outcome);
    } finally {
      setSubmitting(false);
    }
  };

  const handlePinSubmit = async (e: Event) => {
    e.preventDefault();
    setErrorMessage(null);
    if (isLocked()) return;

    const p = pin();
    if (p.length !== 6 || !/^\d{6}$/.test(p)) {
      setErrorMessage('PIN must be exactly 6 digits.');
      return;
    }

    setSubmitting(true);
    try {
      const result = await pinLogin({
        accountId: props.account.accountId,
        pin: p,
      });
      if (!result.ok) {
        setErrorMessage(humanReadableError(result.error));
        return;
      }
      const outcome = result.value;
      switch (outcome.outcome) {
        case 'success':
          setPin('');
          props.onLoggedIn({
            outcome: 'success',
            sessionId: outcome.sessionId,
            accountId: outcome.accountId,
            expiresAtUtc: outcome.expiresAtUtc,
          });
          return;
        case 'failed':
          setPin('');
          setErrorMessage(
            outcome.attemptsRemaining < 0
              ? 'Incorrect PIN.'
              : `Incorrect PIN. ${outcome.attemptsRemaining} ${outcome.attemptsRemaining === 1 ? 'attempt' : 'attempts'} remaining.`,
          );
          return;
        case 'locked':
          setPin('');
          setLockedUntilMs(new Date(outcome.expiresAtUtc).getTime());
          setNow(Date.now());
          return;
        case 'not_configured':
          setErrorMessage('PIN is not set up on this device. Please use your password.');
          flipToPassword();
          return;
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main class="login-screen">
      <div class="login-card">
        <button
          type="button"
          class="login-back"
          onClick={() => props.onCancel()}
          disabled={submitting()}
        >
          ← Back
        </button>

        <div class="login-account">
          <span class="account-tile-avatar" aria-hidden="true">
            {props.account.displayName.slice(0, 1).toUpperCase()}
          </span>
          <h1>{t('login.subtitle', { name: props.account.displayName })}</h1>
        </div>

        <Show
          when={mode() === 'password'}
          fallback={
            <form onSubmit={handlePinSubmit} novalidate data-testid="login-pin-form">
              <label class="field">
                <span class="field-label">PIN</span>
                <input
                  type="password"
                  inputmode="numeric"
                  autocomplete="one-time-code"
                  pattern="\d{6}"
                  maxLength={6}
                  required
                  autofocus
                  class="pin-input"
                  disabled={submitting() || isLocked()}
                  value={pin()}
                  onInput={(e) => {
                    const cleaned = e.currentTarget.value.replace(/\D/g, '').slice(0, 6);
                    setPin(cleaned);
                    e.currentTarget.value = cleaned;
                    setErrorMessage(null);
                  }}
                  data-testid="login-pin-input"
                />
              </label>

              <Show when={isLocked()}>
                <div class="form-error form-error--lockout" role="alert">
                  PIN locked -- try again in {formatRemaining(remainingSeconds())}, or use your
                  password.
                </div>
              </Show>

              <Show when={!isLocked() && errorMessage()}>
                {(msg) => (
                  <div class="form-error" role="alert">
                    {msg()}
                  </div>
                )}
              </Show>

              <button
                type="submit"
                class="primary"
                disabled={submitting() || isLocked() || pin().length !== 6}
                data-testid="login-pin-submit"
              >
                {submitting() ? 'Signing in…' : 'Sign in with PIN'}
              </button>

              <button
                type="button"
                class="login-link"
                onClick={flipToPassword}
                disabled={submitting()}
                data-testid="login-use-password"
              >
                {t('login.usePassword')}
              </button>

              <Show when={props.account.roleCode === 'guardian' && biometricStatus()?.enabled}>
                <button
                  type="button"
                  class="primary login-biometric-btn"
                  onClick={handleBiometricLogin}
                  disabled={submitting() || isLocked()}
                  data-testid="login-use-biometric"
                >
                  {submitting() ? 'Verifying...' : biometricButtonLabel()}
                </button>
              </Show>
            </form>
          }
        >
          <form onSubmit={handlePasswordSubmit} novalidate data-testid="login-password-form">
            <label class="field">
              <span class="field-label">{t('login.passwordLabel')}</span>
              <input
                type="password"
                autocomplete="current-password"
                required
                autofocus
                disabled={submitting() || isLocked()}
                value={password()}
                onInput={(e) => {
                  setPassword(e.currentTarget.value);
                  setErrorMessage(null);
                }}
              />
            </label>

            <Show when={isLocked()}>
              <div class="form-error form-error--lockout" role="alert">
                Locked -- try again in {formatRemaining(remainingSeconds())}.
              </div>
            </Show>

            <Show when={!isLocked() && errorMessage()}>
              {(msg) => (
                <div class="form-error" role="alert">
                  {msg()}
                </div>
              )}
            </Show>

            <button type="submit" class="primary" disabled={submitting() || isLocked()}>
              {submitting() ? 'Signing in…' : t('login.submit')}
            </button>

            <Show when={pinAvailable()}>
              <button
                type="button"
                class="login-link"
                onClick={flipToPin}
                disabled={submitting()}
                data-testid="login-use-pin"
              >
                {t('login.usePin')}
              </button>
            </Show>

            <Show when={props.account.roleCode === 'guardian' && biometricStatus()?.enabled}>
              <button
                type="button"
                class="primary login-biometric-btn"
                onClick={handleBiometricLogin}
                disabled={submitting() || isLocked()}
                data-testid="login-use-biometric"
              >
                {submitting() ? 'Verifying...' : biometricButtonLabel()}
              </button>
            </Show>
          </form>
        </Show>
        <AppVersionFooter />
      </div>
    </main>
  );
};

function humanReadableError(error: AppError): string {
  switch (error.kind) {
    case 'unauthorized':
      return 'Incorrect password.';
    case 'validation':
      return error.message;
    case 'not_found':
      return 'Account not found.';
    case 'network_required':
      return error.message;
    default:
      return `Something went wrong: ${error.message}`;
  }
}

function formatRemaining(seconds: number): string {
  if (seconds < 60) return `${seconds} second${seconds === 1 ? '' : 's'}`;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  if (s === 0) return `${m} minute${m === 1 ? '' : 's'}`;
  return `${m} min ${s} s`;
}
